import { isInViewport } from '../utils/isInViewPort';
import {
    mailListItemAnimationStore,
    clearAnimationStore,
} from 'owa-mail-list-item-animation-store';
import { LoadMoreListViewHandle } from 'owa-loadmore-listview/lib/components/LoadMoreListView';
import { TableView } from 'owa-mail-list-store';
import { canAnimateRows } from './canAnimateRows';
import { getAnimationTiming } from './getAnimationTiming';
import { handleAnimationComplete } from './handleAnimationComplete';
import { getAnimationKeyframes } from './getAnimationKeyframes';
import { getAnimationBackgroundColor } from './getAnimationBackgroundColor';
import { storeInitialRowPositions } from './storeInitialRowPositions';
import { prepareAnimations } from './prepareAnimations';

export const runListAnimation = (
    listView: React.MutableRefObject<LoadMoreListViewHandle>,
    tableView: TableView
) => {
    if (mailListItemAnimationStore.animationRowIds?.size > 0) {
        if (
            canAnimateRows(
                tableView,
                Array.from(mailListItemAnimationStore.animationRowIds),
                mailListItemAnimationStore.animationAction
            )
        ) {
            const prevChildrenPositions = new Map<string /* rowId */, DOMRect /* top offset */>(); // Create maps for the positions of elements
            const currentChildrenPositions = new Map<string /* rowId */, DOMRect /* offset */>();
            const rowItemParts = new Map</*ParentId*/ string, Element[] /* dom child element*/>();
            const animationAction = mailListItemAnimationStore.animationAction;

            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2322 (46,15): Type 'HTMLDivElement | undefined' is not assignable to type 'HTMLDivElement'.
            // @ts-expect-error
            const scrollRegion: HTMLDivElement = listView.current.getScrollRegion();

            const rows = Array.from(scrollRegion.children[0]?.children); // Get the scroll Region children and place them in an array

            if (rows) {
                storeInitialRowPositions(rows, prevChildrenPositions, rowItemParts);
                prepareAnimations(rows, animationAction, rowItemParts, prevChildrenPositions);

                const isInstantAnimation =
                    animationAction == 'PinMiddle' ||
                    animationAction == 'UnpinMiddle' ||
                    animationAction == 'AddRowInitial';

                if (isInstantAnimation) {
                    mailListItemAnimationStore.onAnimationComplete();
                }

                const isFinalPinUnpin =
                    animationAction == 'PinFinal' || animationAction == 'UnpinFinal';
                if (isFinalPinUnpin) {
                    rows.forEach(row => {
                        if (mailListItemAnimationStore.animationRowIds.has(row.id)) {
                            (row as HTMLDivElement).style.cssText = `background: ${getAnimationBackgroundColor(
                                animationAction
                            )}`;
                        }
                    });
                }

                if (animationAction === 'AddRowFinal') {
                    rows.forEach(row => {
                        if (mailListItemAnimationStore.animationRowIds.has(row.id)) {
                            (row as HTMLDivElement).style.cssText =
                                'display: inherit; background:' +
                                getAnimationBackgroundColor(
                                    mailListItemAnimationStore.animationAction
                                ) +
                                ';';
                        }
                    });
                }

                rows.forEach(row => {
                    // When we read the position, the dom snapshot will update, so we can get the desired "new" state with the item out of the flow
                    currentChildrenPositions.set(row.id, row.getBoundingClientRect());
                    rowItemParts.get(row.id)?.forEach(itemPart => {
                        currentChildrenPositions.set(itemPart.id, itemPart.getBoundingClientRect());
                    });
                });

                let animatedItems = 0;
                const scrollRegionRect = scrollRegion.getBoundingClientRect();
                const parentRowChange = new Map<string /* rowId */, number /* top */>(); // store the parents directional change

                const animateRow = (row: Element, isItemPart: boolean = false) => {
                    const isAnimatedRow = mailListItemAnimationStore.animationRowIds.has(row.id);
                    if (
                        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                        // -> Error TS2345 (166,34): Argument of type 'DOMRect | undefined' is not assignable to parameter of type 'DOMRect'.
                        // @ts-expect-error
                        isInViewport(currentChildrenPositions.get(row.id), scrollRegionRect) ||
                        (isAnimatedRow && isFinalPinUnpin)
                    ) {
                        // Check if the element is visible before animating
                        if (!isAnimatedRow) {
                            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                            // -> Error TS2532 (174,42): Object is possibly 'undefined'.
                            // @ts-expect-error
                            const firstBox = prevChildrenPositions.get(row.id).top;
                            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                            // -> Error TS2532 (178,41): Object is possibly 'undefined'.
                            // @ts-expect-error
                            const lastBox = currentChildrenPositions.get(row.id).top;
                            if (firstBox && lastBox) {
                                const changeY =
                                    firstBox - lastBox - (parentRowChange.get(row.id) ?? 0); // If an itemPart it needs to move separately
                                if (!isItemPart) {
                                    parentRowChange.set(row.id, changeY);
                                }
                                const player = (row as HTMLDivElement)?.animate(
                                    // TODO add other movements -- for pin and make this a helper function.
                                    [
                                        { transform: 'translateY(' + changeY + 'px)', offset: 0 },
                                        {
                                            transform: 'translateY(' + changeY + 'px)',
                                            offset: 0.25,
                                        },
                                        { transform: 'translateY(0)' },
                                    ],
                                    {
                                        duration: isInstantAnimation ? 35 : 433,
                                        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
                                    }
                                );
                                if (animatedItems == 0) {
                                    player.onfinish = () => {
                                        handleAnimationComplete(animationAction, rows);
                                    };
                                    animatedItems = animatedItems + 1;
                                }
                            }
                        } else {
                            if (!isInstantAnimation) {
                                (row.children[0] as HTMLDivElement)?.animate(
                                    getAnimationKeyframes(animationAction),
                                    getAnimationTiming(animationAction)
                                );
                            }
                        }
                    }
                };
                rows.forEach(row => {
                    animateRow(row);
                    rowItemParts.get(row.id)?.forEach(itemPart => {
                        animateRow(itemPart, true /* isItemPart */);
                    });
                });
            }
        } else {
            mailListItemAnimationStore.onAnimationComplete?.();
            clearAnimationStore();
        }
    }
};
